import { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext(null);
const STORAGE_KEY = "hospice-theme";

function computeIsDark(mode) {
  const prefersDark = window.matchMedia?.("(prefers-color-scheme: dark)").matches;
  return mode === "dark" || (mode === "system" && prefersDark);
}

function applyTheme(mode) {
  const root = document.documentElement;
  const isDark = computeIsDark(mode);
  root.classList.toggle("dark", isDark);
  root.dataset.theme = isDark ? "dark" : "light";
}

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => localStorage.getItem(STORAGE_KEY) || "system");
  // Resolved theme (either 'light' or 'dark') — consumers use this to pick
  // between light/dark color variants on Visual Editor sections.
  const [resolvedMode, setResolvedMode] = useState(() => computeIsDark(mode) ? "dark" : "light");

  const setModeAndPersist = useCallback((next) => {
    localStorage.setItem(STORAGE_KEY, next);
    setMode(next);
  }, []);

  useEffect(() => {
    applyTheme(mode);
    setResolvedMode(computeIsDark(mode) ? "dark" : "light");
  }, [mode]);

  // Listen for OS theme changes when following system
  useEffect(() => {
    if (mode !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      applyTheme("system");
      setResolvedMode(computeIsDark("system") ? "dark" : "light");
    };
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, resolvedMode, setMode: setModeAndPersist }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
